import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FilePicker,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { useSupplyAppsMutations } from '../../hooks';

export function AppImportDialog({ onClose }: { onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const mutation = useSupplyAppsMutations().import;
  const dialogGuard = useBusyDialogGuard(mutation.isPending, onClose);

  async function submit() {
    if (!file) return;
    try {
      await mutation.mutateAsync(file);
      toast.success(dashboardT('supplyApps.importSuccess'));
      onClose();
    } catch (error) {
      showRequestError(error, dashboardT('supplyApps.importError'));
    }
  }

  return (
    <Dialog open onOpenChange={dialogGuard.onOpenChange}>
      <DialogContent
        className="max-w-lg gap-0 p-0"
        onEscapeKeyDown={dialogGuard.onDismissInteraction}
        onPointerDownOutside={dialogGuard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle className="text-[15px]">{dashboardT('supplyApps.importTitle')}</DialogTitle>
          <DialogDescription>{dashboardT('supplyApps.importDescription')}</DialogDescription>
        </DialogHeader>
        <div className="px-5 py-5">
          <FilePicker
            label={dashboardT('supplyApps.csvFile')}
            buttonLabel={dashboardT('supplyApps.chooseFile')}
            accept=".csv,text/csv"
            value={file}
            onFileChange={setFile}
            required
            disabled={mutation.isPending}
          />
        </div>
        <DialogFooter className="border-border border-t px-5 py-4">
          <Button
            type="button"
            variant="outline"
            disabled={mutation.isPending}
            onClick={dialogGuard.close}
          >
            {dashboardT('supplyApps.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!file || mutation.isPending}
            onClick={() => void submit()}
          >
            {dashboardT(mutation.isPending ? 'supplyApps.importing' : 'supplyApps.import')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
